import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { formatClock } from "../../course";
import { useDensity } from "../../../store/density";
import { hasMessage, useI18n } from "../../../i18n";
import type { Translate } from "../../../i18n";
import { useFootprintsApi } from "../api/footprintsApi";
import type { Achievements, Footprints, FootprintRow, SortOrder } from "../types";
import { BADGE_KEYS, GOAL_KEYS, badgeIcon } from "../lib/badges";
import {
  filterRows,
  footprintRows,
  presetsIn,
  shortDate,
  sortRows,
  versionInsight,
} from "../lib/logic";
import "../../../styles/footprints.css";

/**
 * FS-10-01 나의 발자취(/me) — SCR v0.6 sfs-10(SE) · sfs-10-su(SU) 와이어.
 *  - 왼쪽 '내 도전 기록': 스냅샷 버전별 기록(버전 칩 · 코스 · 완주/미완주 · 내 시간 또는 시도 · ▶ 보기 → FS-14 학생 변형).
 *    SE: 내 기계 · 코스 · 결과 · 시간/도전 · 큰 '▶ 보기'(≥64px). SU: 날짜 · 내 기계 · 코스 · 결과 · 시간 · 시도 · ▶ +
 *    '정렬: 최근순 ▾ (내 기록 안에서만)' · '필터: 코스 ▾'. 아래 안내(ℹ — 버전이 바뀌면 결과가 달라진다 · 비교 없음).
 *  - 오른쪽 '내 배지'·'우리 반 목표'(P2-S6 · SR-ACH-02 서버 권위 — GET …/me/achievements): 배지 10종을 늘 다 그린다
 *    (받은 것 진하게 · 안 받은 것 흐리게 — 개수·순위 없음 · 순서 고정). 반 공동 목표 3종 진행률 바(SE 이름만 · SU 이름 + %)
 *    · '누가 했는지는 안 보여요'. 못 받으면 흐린 자리 + 중립 안내(기록 표는 그대로).
 *  - 상태: 기록 없음(피코 + 코스 도전) · 기록 있음. 남의 기록·평균·순위·'최고 기록' 없음(SR-REC-03 · DR-01) · 입력 없음(DR-02).
 */
type Load =
  | { status: "loading" }
  | { status: "ready"; fp: Footprints }
  | { status: "error"; code: ErrorCode };

type AchLoad = { status: "loading" } | { status: "ready"; a: Achievements } | { status: "error" };

const courseName = (t: Translate, key: string) =>
  hasMessage(`course.preset.${key}`) ? t(`course.preset.${key}`) : key;

export function FootprintsScreen() {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const api = useFootprintsApi();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const [order, setOrder] = useState<SortOrder>("recent");
  const [preset, setPreset] = useState<string | null>(null);
  const [ach, setAch] = useState<AchLoad>({ status: "loading" });

  useTopBar({ icon: "👣", title: t("footprints.title") });

  useEffect(() => {
    if (!api) return;
    let alive = true;
    setLoad({ status: "loading" });
    api.mine().then(
      (fp) => alive && setLoad({ status: "ready", fp }),
      (e) => alive && setLoad({ status: "error", code: toApiError(e).code }),
    );
    // 배지·반 목표는 따로 — 못 받아도 기록 표는 그대로 보인다
    setAch({ status: "loading" });
    api.achievements().then(
      (a) => alive && setAch({ status: "ready", a }),
      () => alive && setAch({ status: "error" }),
    );
    return () => {
      alive = false;
    };
  }, [api, seq]);

  const all = useMemo(() => (load.status === "ready" ? footprintRows(load.fp) : []), [load]);
  const rows = useMemo(() => sortRows(filterRows(all, preset), order), [all, preset, order]);
  const insight = useMemo(() => versionInsight(all), [all]);

  if (load.status === "error")
    return (
      <ScreenRoot id="FS-10-01" title={t("footprints.title")} hideTitle>
        <FeBanner
          code={load.code}
          audience="student"
          actions={[{ label: t("common.retry"), icon: "⟲", onClick: () => setSeq((n) => n + 1) }]}
        />
      </ScreenRoot>
    );

  const fp = load.status === "ready" ? load.fp : null;
  const empty = !!fp && all.length === 0;
  const watch = (r: FootprintRow) =>
    r.replayId ? () => navigate(`/replay/${encodeURIComponent(r.replayId!)}`) : undefined;

  const records = (
    <section
      className="pf-pane pf-pane--hero pf-fp__records"
      aria-labelledby="fp-records"
      aria-busy={load.status === "loading"}
      data-testid="fp-records"
    >
      <div className="pf-fp__head">
        <h2 id="fp-records" className="pf-h2">
          {t("footprints.records")}
        </h2>
        {!se && !empty && fp && (
          <>
            <label className="pf-fp__control">
              <span>{t("footprints.sort")}</span>
              <select
                value={order}
                onChange={(e) => setOrder(e.target.value as SortOrder)}
                data-testid="fp-sort"
              >
                <option value="recent">{t("footprints.sortRecent")}</option>
                <option value="oldest">{t("footprints.sortOldest")}</option>
              </select>
              <span className="pf-fp__scope">{t("footprints.sortScope")}</span>
            </label>
            <label className="pf-fp__control pf-fp__control--end">
              <span>{t("footprints.filter")}</span>
              <select
                value={preset ?? ""}
                onChange={(e) => setPreset(e.target.value || null)}
                data-testid="fp-filter"
              >
                <option value="">{t("footprints.filterAll")}</option>
                {presetsIn(all).map((k) => (
                  <option key={k} value={k}>
                    {courseName(t, k)}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>
      {load.status === "loading" && <PicoBubble text={t("footprints.loading")} busy live={false} />}
      {empty && (
        <div className="pf-fp__empty" data-testid="fp-empty">
          <PicoBubble text={t("footprints.empty")} avatar="👣" live={false} />
          <ButtonLink to="/course" variant="accent" icon={se ? "⛰" : undefined}>
            {t("footprints.toCourse")}
          </ButtonLink>
        </div>
      )}
      {!empty && fp && (
        <table className={`pf-tbl pf-fp__table pf-fp__table--${density}`} data-testid="fp-table">
          <thead>
            <tr>
              {!se && <th scope="col">{t("footprints.col.date")}</th>}
              <th scope="col">{t("footprints.col.version")}</th>
              <th scope="col">{t("footprints.col.course")}</th>
              <th scope="col">{t("footprints.col.result")}</th>
              {se ? (
                <th scope="col">
                  <span className="pf-sr-only">{t("footprints.col.timeOrTries")}</span>
                </th>
              ) : (
                <>
                  <th scope="col">{t("footprints.col.time")}</th>
                  <th scope="col">{t("footprints.col.tries")}</th>
                </>
              )}
              <th scope="col">
                <span className="pf-sr-only">{t("footprints.col.replay")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const course = courseName(t, r.presetKey);
              const time = r.timeMs != null ? formatClock(r.timeMs / 1000, !se) : null;
              const onWatch = watch(r);
              const label = t("footprints.watchAria", {
                version: r.version,
                course,
              });
              return (
                <tr
                  key={r.attemptId}
                  data-testid="fp-row"
                  data-version={r.version}
                  data-completed={r.completed}
                  data-preset={r.presetKey}
                >
                  {!se && <td className="pf-fp__date">{shortDate(r.createdAt)}</td>}
                  <td>
                    {se ? (
                      <span className="pf-fp__ver">
                        {t("footprints.version", { n: r.version })}
                      </span>
                    ) : (
                      <b>{t("footprints.version", { n: r.version })}</b>
                    )}
                  </td>
                  <td>
                    <b>{se ? t("footprints.courseSE", { course }) : course}</b>
                  </td>
                  <td>
                    <span
                      className={`pf-chip-static${r.completed ? " pf-chip-static--ok" : ""}`}
                      data-testid="fp-result"
                    >
                      {r.completed
                        ? t(se ? "footprints.doneSE" : "footprints.done")
                        : t("footprints.notDone")}
                    </span>
                  </td>
                  {se ? (
                    <td className="pf-fp__num" data-testid="fp-time">
                      {time ?? t("footprints.triesSE", { n: r.tries })}
                    </td>
                  ) : (
                    <>
                      <td className="pf-fp__num" data-testid="fp-time">
                        <b>{time ?? "—"}</b>
                      </td>
                      <td className="pf-fp__num" data-testid="fp-tries">
                        <b>{r.tries}</b>
                      </td>
                    </>
                  )}
                  <td className="pf-fp__act">
                    <Button
                      variant="ghost"
                      className={se ? "pf-fp__watch" : "pf-fp__watch pf-fp__watch--su"}
                      disabled={!onWatch}
                      onClick={onWatch}
                      aria-label={label}
                      data-testid="fp-watch"
                    >
                      {se ? `▶ ${t("footprints.watch")}` : "▶"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!empty && fp && (
        <p className="pf-course__note pf-fp__note" data-testid="fp-note">
          {"ℹ "}
          {se
            ? t("footprints.noteSE")
            : [
                insight
                  ? t(
                      insight.failPattern && hasMessage(`footprints.change.${insight.failPattern}`)
                        ? "footprints.insightChange"
                        : "footprints.insight",
                      {
                        from: insight.from,
                        to: insight.to,
                        course: courseName(t, insight.presetKey),
                        change: insight.failPattern
                          ? t(`footprints.change.${insight.failPattern}`)
                          : "",
                      },
                    )
                  : null,
                t("footprints.onlyMineSU"),
              ]
                .filter(Boolean)
                .join(" ")}
        </p>
      )}
    </section>
  );

  const achieved = ach.status === "ready" ? ach.a : null;
  const badges =
    achieved?.badges ??
    BADGE_KEYS.map((key) => ({ key, icon: badgeIcon(key), earned: false, earnedAt: null }));
  const goals =
    achieved?.classGoals ??
    // 못 읽었을 때 자리 — relay-done 은 서버가 줄 때만(PH3 릴레이 코스)
    GOAL_KEYS.filter((k) => k !== "relay-done").map((key) => ({ key, pct: null as number | null }));
  const achOff = ach.status !== "ready";

  return (
    <ScreenRoot
      id="FS-10-01"
      title={t("footprints.title")}
      hideTitle
      className={`pf-fp pf-fp--${density}`}
    >
      <div className="pf-fp__grid">
        {records}
        <div className="pf-fp__side">
          <section
            className={`pf-pane pf-fp__badges${achOff ? " pf-fp__off" : ""}`}
            aria-labelledby="fp-badges"
            aria-busy={ach.status === "loading"}
            data-testid="fp-badges"
            data-state={ach.status}
          >
            <h2 id="fp-badges" className="pf-h2">
              {t(se ? "footprints.badgesSE" : "footprints.badgesSU")}
            </h2>
            <ul className="pf-fp__badge-list">
              {badges.map((b) => {
                const name = t(`footprints.badge.${b.key}`);
                return (
                  <li
                    key={b.key}
                    className={`pf-fp__badge${b.earned ? " pf-fp__badge--on" : ""}`}
                    data-badge={b.key}
                    data-earned={b.earned}
                    title={name}
                  >
                    <span aria-hidden="true">{b.icon}</span>
                    <span className="pf-sr-only">
                      {t(b.earned ? "footprints.badgeEarned" : "footprints.badgeNotYet", { name })}
                    </span>
                  </li>
                );
              })}
            </ul>
            {ach.status === "error" && (
              <p className="pf-muted" data-testid="fp-ach-off">
                {t("footprints.achOff")}
              </p>
            )}
          </section>
          <section
            className={`pf-pane pf-fp__goals${achOff ? " pf-fp__off" : ""}`}
            aria-labelledby="fp-goals"
            data-testid="fp-goals"
          >
            <h2 id="fp-goals" className="pf-h2">
              {t("footprints.goal")}
            </h2>
            {goals.map((g) => {
              const name = t(`footprints.goals.${g.key}`);
              return (
                <div key={g.key} className="pf-fp__goal" data-goal={g.key}>
                  <p className="pf-muted">
                    {!se && g.pct != null ? t("footprints.goalPct", { name, pct: g.pct }) : name}
                  </p>
                  <div
                    className="pf-fp__bar"
                    role={g.pct != null ? "progressbar" : undefined}
                    aria-valuenow={g.pct ?? undefined}
                    aria-valuemin={g.pct != null ? 0 : undefined}
                    aria-valuemax={g.pct != null ? 100 : undefined}
                    aria-label={name}
                  >
                    <span style={{ width: `${g.pct ?? 0}%` }} />
                  </div>
                </div>
              );
            })}
            <p className="pf-muted">{t("footprints.goalAnon")}</p>
          </section>
        </div>
      </div>
    </ScreenRoot>
  );
}
